import React, { useState } from 'react';
import { Text } from '../../../globalStyles';
import { CampusResourcesQuery, ResourceType } from '../../../graphQL';
import { getClickableLink } from '../../../utils';
import { ResourceModal } from '../Modals/ResourceModal';
import { SectionRow, SectionRowItem } from './SectionRow';

type Resource = CampusResourcesQuery['organization']['resources'][number];

type ResourceMetadata = {
  title: string;
  description?: string;
};

type ResourceSectionProps = {
  organizationId: number;
  editable?: boolean;
  verticalDisplay?: boolean;
  resources: CampusResourcesQuery['organization']['resources'];
  refetch: () => Promise<unknown>;
  metadata: Partial<Record<ResourceType, ResourceMetadata>>;
  resourceTypes: ResourceType[];
  sectionKeyPrefix: string;
  resourceValidator?: (resourceType: ResourceType) => boolean;
  createOtherSection?: (resources: Resource[]) => {
    label: string;
    info?: string;
    category: ResourceType;
    resources: Resource[];
  };
};

export function ResourceSection({
  organizationId,
  editable = false,
  verticalDisplay = false,
  resources,
  refetch,
  metadata,
  resourceTypes,
  sectionKeyPrefix,
  resourceValidator,
  createOtherSection,
}: ResourceSectionProps) {
  const [modalResource, setModalResource] = useState<Resource | 'new' | null>(null);
  const [modalResourceType, setModalResourceType] = useState<Partial<ResourceType> | null>(null);

  // show this if provider is not Mantra Admin and there are no resources of a particular type
  const NoResources = () => {
    if (editable) {
      return null;
    }
    return (
      <SectionRowItem>
        <Text.bodySmallItalicized>None listed</Text.bodySmallItalicized>
      </SectionRowItem>
    );
  };

  const openAddResourceModal = (category: Partial<ResourceType>) => {
    setModalResource('new');
    setModalResourceType(category);
  };

  const closeResourceModal = () => {
    setModalResource(null);
    setModalResourceType(null);
  };

  type AddResourceProp = {
    category: Partial<ResourceType>;
  };
  const AddResource = ({ category }: AddResourceProp) => {
    if (!editable) {
      return null;
    }
    return (
      <SectionRowItem>
        <Text.linkButton onClick={() => openAddResourceModal(category)}>
          + Add Resource
        </Text.linkButton>
      </SectionRowItem>
    );
  };

  const onEdit = editable
    ? (id: number) => () => {
        const filteredResource = resources.find(({ id: resourceId }) => resourceId === id);
        if (!filteredResource) {
          setModalResource('new');
          return;
        }
        if (resourceValidator ? resourceValidator(filteredResource.resourceType) : true) {
          setModalResource(filteredResource);
          return;
        }
        setModalResource({ ...filteredResource, resourceType: ResourceType.Other }!);
      }
    : () => undefined;

  const renderResources = (
    resourcesToRender: CampusResourcesQuery['organization']['resources']
  ) => {
    if (resourcesToRender.length <= 0) {
      return <NoResources />;
    }
    return resourcesToRender.map(filteredResource => (
      <SectionRowItem
        key={`${sectionKeyPrefix}-resource-row-${filteredResource.id}`}
        onEditClick={onEdit(filteredResource.id)}
      >
        <Text.bodyBold>{filteredResource.name}</Text.bodyBold>
        {filteredResource.description ? (
          <Text.bodySmall>Description: {filteredResource.description}</Text.bodySmall>
        ) : null}
        {filteredResource.contact ? (
          <Text.bodySmall>Contact: {filteredResource.contact}</Text.bodySmall>
        ) : null}
        {filteredResource.email ? (
          <Text.bodySmall>Email: {filteredResource.email}</Text.bodySmall>
        ) : null}
        {filteredResource.hours ? (
          <Text.bodySmall>Hours: {filteredResource.hours}</Text.bodySmall>
        ) : null}
        {filteredResource.website ? (
          <Text.bodySmall>
            Website:{' '}
            <Text.externalLink href={getClickableLink(filteredResource.website)} target="_blank">
              {filteredResource.website}
            </Text.externalLink>
          </Text.bodySmall>
        ) : null}
      </SectionRowItem>
    ));
  };

  // Helper to create section object with metadata
  const createSection = (resourceType: ResourceType) => ({
    label: metadata[resourceType]?.title || resourceType,
    info: metadata[resourceType]?.description,
    category: resourceType,
    resources: resources.filter(({ resourceType: rt }) => rt === resourceType),
  });

  // Create sections for all resource types
  const standardSections = resourceTypes.map(createSection);

  // Add other section if provided
  const sections = createOtherSection
    ? [...standardSections, createOtherSection(resources)]
    : standardSections;

  return (
    <>
      {sections.map((section, idx) => (
        <SectionRow
          key={`${idx}-${section.category}`}
          verticalDisplay={verticalDisplay}
          label={section.label}
          info={section.info}
        >
          {renderResources(section.resources)}
          <AddResource category={section.category} />
        </SectionRow>
      ))}
      {modalResource && (
        <ResourceModal
          modalResourceType={modalResourceType}
          organizationId={organizationId}
          resource={modalResource === 'new' ? undefined : modalResource}
          onClose={() => closeResourceModal()}
          refetch={refetch}
        />
      )}
    </>
  );
}
